import React, { useState, useEffect, useRef } from 'react';
import './VideoList.css';
import PropTypes from 'prop-types';

function VideoList({ videos, onProcessVideo, onProcessAllVideos }) {
  const [expandedVideoId, setExpandedVideoId] = useState(null);
  const [lastProcessedId, setLastProcessedId] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'processed', 'pending'
  const [searchTerm, setSearchTerm] = useState('');
  const [isProcesandoTodos, setIsProcesandoTodos] = useState(false);
  const [progreso, setProgreso] = useState({ total: 0, completados: 0, fallidos: 0 });
  const statusRefs = useRef({});
  
  // Función para generar un color aleatorio para las miniaturas
  const getRandomColor = () => {
    const colors = [
      '#23eaed','#087d92' ,'#1894ac'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };
  
  // Asignar colores aleatorios a cada video (una sola vez)
  const [videoColors] = useState(() => {
    const colors = {};
    videos.forEach(video => {
      colors[video.id] = getRandomColor();
    });
    return colors;
  });
  
  // Efecto para animar el cambio de estado
  useEffect(() => {
    if (lastProcessedId && statusRefs.current[lastProcessedId]) {
      const statusElement = statusRefs.current[lastProcessedId];
      statusElement.classList.add('status-changed');
      
      // Eliminar la clase después de la animación
      const timer = setTimeout(() => {
        statusElement.classList.remove('status-changed');
      }, 600);
      
      return () => clearTimeout(timer);
    }
  }, [lastProcessedId]);

  // Función para alternar la expansión de un video
  const toggleVideoExpansion = (videoId) => {
    setExpandedVideoId(expandedVideoId === videoId ? null : videoId);
  };

  // Función para manejar el procesamiento de un video
  const handleProcessVideo = (videoId, event) => {
    // Evitar que el clic se propague al contenedor del video (que colapsaría el video)
    event.stopPropagation();
    onProcessVideo(videoId);
    setLastProcessedId(videoId);
  };

  const handleProcesarTodos = async (event) => {
    event.stopPropagation();
    const pendientes = videos.filter(v => !v.processed);
    if (pendientes.length === 0) return;

    const sinTranscripcion = pendientes.filter(v => v.hasTranscription === false);
    const conTranscripcion = pendientes.filter(v => v.hasTranscription !== false);

    if (conTranscripcion.length === 0) {
      alert('Ningún video pendiente tiene transcripción. Sube los archivos .docx al SharePoint antes de procesar.');
      return;
    }

    setIsProcesandoTodos(true);
    setProgreso({ total: conTranscripcion.length, completados: 0, fallidos: 0 });

    let completados = 0;
    let fallidos = 0;

    for (const video of conTranscripcion) {
      try {
        await onProcessAllVideos(video.id);
        completados++;
        setLastProcessedId(video.id);
      } catch {
        fallidos++;
      }
      setProgreso({ total: conTranscripcion.length, completados, fallidos });
    }

    setIsProcesandoTodos(false);

    let mensaje = `Proceso completado: ${completados} procesados correctamente, ${fallidos} fallaron.`;
    if (sinTranscripcion.length > 0) {
      mensaje += `\n${sinTranscripcion.length} video(s) omitidos por no tener transcripción en SharePoint.`;
    }
    alert(mensaje);
  };

  // Filtrar videos según los criterios de búsqueda y filtro
  const filteredVideos = videos.filter(video => {
    // Filtrar por estado
    if (filter === 'processed' && !video.processed) return false;
    if (filter === 'pending' && video.processed) return false;

    // Filtrar por término de búsqueda
    return !(searchTerm && !video.title.toLowerCase().includes(searchTerm.toLowerCase()));
  });

  const pendientesCount = videos.filter(v => !v.processed).length;

  return (
    <div className="video-list-container">
      <h2>Lista de Videos</h2>
      
      <div className="video-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Buscar videos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <i className="fas fa-search"></i>
        </div>
        
        <div className="filter-buttons">
          <button 
            className={filter === 'all' ? 'active' : ''}
            onClick={() => setFilter('all')}
          >
            Todos
          </button>
          <button 
            className={filter === 'pending' ? 'active' : ''}
            onClick={() => setFilter('pending')}
          >
            Pendientes
          </button>
          <button 
            className={filter === 'processed' ? 'active' : ''}
            onClick={() => setFilter('processed')}
          >
            Procesados
          </button>
        </div>

        {pendientesCount > 0 && filter === 'pending' && (
          <button
            className="process-all-button"
            onClick={handleProcesarTodos}
            disabled={isProcesandoTodos}
          >
            <i className="fas fa-cogs"></i>{' '}
            {isProcesandoTodos
              ? `Procesando... ${progreso.completados}/${progreso.total}`
              : `Procesar Todos (${pendientesCount} pendientes)`}
          </button>
        )}
      </div>

      {filteredVideos.length === 0 ? (
        <div className="empty-list">
          {searchTerm || filter !== 'all' ? (
            <>
              <i className="fas fa-filter"></i>
              <p>No se encontraron videos con los filtros actuales</p>
              <button 
                className="clear-filters-button"
                onClick={() => {
                  setSearchTerm('');
                  setFilter('all');
                }}
              >
                Limpiar filtros
              </button>
            </>
          ) : (
            <>
              <i className="far fa-folder-open"></i>
              <p>No hay videos disponibles</p>
            </>
          )}
        </div>
      ) : (
        <div className="video-grid">
          {filteredVideos.map(video => (
            <button 
              key={video.id} 
              className={`video-card ${expandedVideoId === video.id ? 'expanded' : ''}`}
              onClick={() => toggleVideoExpansion(video.id)}
            >
              <div className="video-card-header">
                <i className={`far ${video.processed ? 'fa-check-circle' : 'fa-play-circle'}`}></i>
                <h3 className="video-title">{video.title}</h3>
                <span 
                  ref={el => statusRefs.current[video.id] = el}
                  className={`video-status-badge ${video.processed ? 'processed' : 'unprocessed'}`}
                >
                  {video.processed ? 'Procesado' : 'Pendiente'}
                </span>
              </div>
              
              <div className="video-card-content">
                <div 
                  className="video-thumbnail"
                  style={{ backgroundColor: videoColors[video.id] || getRandomColor() }}
                >
                  <i className="fas fa-film"></i>
                </div>
                
                {expandedVideoId === video.id && (
                  <div className="video-card-details">
                    <div className="video-info">
                      <p><strong>Nombre:</strong> {video.title}</p>
                      <p><strong>Estado:</strong> {video.processed ? 'Procesado' : 'Pendiente'}</p>
                      {video.webUrl && (
                        <p>
                          <strong>Ver en SharePoint:</strong>{' '}
                          <a 
                            href={video.webUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Abrir enlace <i className="fas fa-external-link-alt"></i>
                          </a>
                        </p>
                      )}
                    </div>
                    
                    {!video.processed && (
                      video.hasTranscription === false ? (
                        <p className="transcription-warning">
                          <i className="fas fa-exclamation-triangle"></i> Sin transcripción: sube el archivo .docx al SharePoint para poder procesar este video.
                        </p>
                      ) : (
                        <button
                          className="process-button"
                          onClick={(e) => handleProcessVideo(video.id, e)}
                        >
                          <i className="fas fa-cog"></i> Procesar Video
                        </button>
                      )
                    )}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
      
      {filteredVideos.length > 0 && (
        <div className="video-count">
          Mostrando {filteredVideos.length} de {videos.length} videos
        </div>
      )}
    </div>
  );
}

// PropTypes validation
VideoList.propTypes = {
  videos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      processed: PropTypes.bool,
      hasTranscription: PropTypes.bool,
      webUrl: PropTypes.string,
    })
  ).isRequired,
  onProcessVideo: PropTypes.func.isRequired,
  onProcessAllVideos: PropTypes.func.isRequired,
};

export default VideoList;